// Componente para mostrar alertas de cambio de precio en el Dashboard
import React, { useState, useEffect } from 'react';
import { getAuthToken } from '../api/api';
import { AlertTriangle, Check, X, TrendingUp, TrendingDown, Package } from 'lucide-react';

const AlertasCambioPrecio = () => {
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [procesando, setProcesando] = useState(null);

  useEffect(() => {
    fetchAlertas();
  }, []);

  const fetchAlertas = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/alertas-precio?estado=pendiente`,
        {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAlertas(data);
      }
    } catch (error) {
      console.error('Error fetching alertas:', error);
    } finally {
      setLoading(false);
    }
  };

  const resolverAlerta = async (alertaId, accion, actualizarCatalogo = false) => {
    setProcesando(alertaId);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/alertas-precio/${alertaId}/resolver`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            accion,
            actualizar_catalogo: actualizarCatalogo,
            notas: accion === 'aprobar' && actualizarCatalogo 
              ? 'Precio actualizado en catálogo' 
              : accion === 'rechazar' 
                ? 'Precio no actualizado - Se mantendrá precio anterior' 
                : 'Precio aceptado solo para esta recepción'
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        // Remover alerta de la lista
        setAlertas(alertas.filter(a => a.id !== alertaId));
        
        // Mostrar mensaje
        if (actualizarCatalogo) {
          alert(`✅ ${result.message}`);
        }
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error resolviendo alerta:', error);
      alert('Error al procesar la alerta');
    } finally {
      setProcesando(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (alertas.length === 0) {
    return null; // No mostrar nada si no hay alertas
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-orange-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            <h3 className="text-base sm:text-lg font-semibold text-white">
              Alertas de Cambio de Precio
            </h3>
          </div>
          <span className="bg-white bg-opacity-20 text-white text-xs sm:text-sm font-medium px-2 sm:px-3 py-1 rounded-full">
            {alertas.length} pendiente{alertas.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Lista de alertas */}
      <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
        {alertas.map((alerta) => {
          const esMasCaro = alerta.precio_nuevo > alerta.precio_anterior;
          
          return (
            <div key={alerta.id} className="p-3 sm:p-4 hover:bg-gray-50 transition-colors">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                {/* Info del ingrediente */}
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Package className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-900 text-sm sm:text-base">
                      {alerta.ingrediente_nombre}
                    </span>
                    {esMasCaro ? (
                      <span className="flex items-center text-red-600 text-xs sm:text-sm">
                        <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        +{alerta.diferencia_porcentaje}%
                      </span>
                    ) : (
                      <span className="flex items-center text-green-600 text-xs sm:text-sm">
                        <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        -{alerta.diferencia_porcentaje}%
                      </span>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                    <div>
                      <span className="text-gray-500">Anterior:</span>{' '}
                      <span className="font-medium">€{alerta.precio_anterior?.toFixed(2)}/kg</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Nuevo:</span>{' '}
                      <span className={`font-medium ${esMasCaro ? 'text-red-600' : 'text-green-600'}`}>
                        €{alerta.precio_nuevo?.toFixed(2)}/kg
                      </span>
                    </div>
                  </div>
                  
                  {alerta.proveedor_nombre && (
                    <p className="text-xs text-gray-500 mt-1">
                      Proveedor: {alerta.proveedor_nombre}
                    </p>
                  )}
                </div>

                {/* Botones de acción */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-2 w-full sm:w-auto">
                  {/* Aprobar y actualizar catálogo */}
                  <button
                    onClick={() => resolverAlerta(alerta.id, 'aprobar', true)}
                    disabled={procesando === alerta.id}
                    className="flex items-center justify-center space-x-1 px-3 py-2 bg-green-600 text-white text-xs sm:text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors w-full sm:w-auto"
                    title="Aprobar y actualizar precio en catálogo"
                  >
                    <Check className="w-4 h-4" />
                    <span>Actualizar catálogo</span>
                  </button>
                  
                  {/* Rechazar (mantener precio anterior) */}
                  <button
                    onClick={() => resolverAlerta(alerta.id, 'rechazar', false)}
                    disabled={procesando === alerta.id}
                    className="flex items-center justify-center space-x-1 px-3 py-2 bg-red-100 text-red-700 text-xs sm:text-sm rounded-lg hover:bg-red-200 disabled:opacity-50 transition-colors w-full sm:w-auto"
                    title="Mantener precio anterior en catálogo"
                  >
                    <X className="w-4 h-4" />
                    <span>Mantener anterior</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer con explicación */}
      <div className="bg-gray-50 px-4 sm:px-6 py-3 border-t border-gray-100">
        <p className="text-xs text-gray-500">
          💡 <strong>Actualizar catálogo:</strong> El nuevo precio se usará para todos los cálculos futuros.{' '}
          <strong>Mantener anterior:</strong> El precio del catálogo no cambiará (útil si puedes negociar con el proveedor).
        </p>
      </div>
    </div>
  );
};

export default AlertasCambioPrecio;
